import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { SparklesIcon, QuestionMarkCircleIcon, ArrowPathIcon, CheckCircleIcon, PlayIcon, TrashIcon, PlusCircleIcon, ArrowLeftIcon, StarIcon } from '@heroicons/react/24/solid';
import UrlConfig from '../../../data/UrlConfig';

const timeFrames = [
    { label: '20 años', years: 20 }, { label: '15 años', years: 15 },
    { label: '10 años', years: 10 }, { label: '7 años', years: 7 },
    { label: '5 años', years: 5 }, { label: '3 años', years: 3 },
    { label: '1 año', years: 1 }, { label: '6 meses', years: 0.5 },
    { label: '3 meses', years: 0.25 }, { label: '1 mes', years: 0.083 },
];

const BeliefThresholdActivity = () => {
    const { user } = useAuth();
    const activityId = 'umbral-de-creencia';

    // <-- MODIFICADO: No se llama a UrlConfig() como función

    const [phase, setPhase] = useState('loading');
    const [allGoals, setAllGoals] = useState([]);
    const [currentGoal, setCurrentGoal] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    
    const beliefInputRef = useRef(null);

    useEffect(() => {
        if (!user) { setPhase('goalManagement'); return; }
        const loadGoals = async () => {
            setPhase('loading');
            try {
                // <-- MODIFICADO: Se usa '&' para el query parameter y se llama a UrlConfig directamente
                const response = await fetch(`${UrlConfig.activity_getBeliefThreshold}&activityId=${activityId}`, { credentials: 'include' });
                if (response.ok) {
                    const result = await response.json();
                    setAllGoals(result.success && Array.isArray(result.data) ? result.data : []);
                }
            } catch (error) { console.error("Error al cargar los objetivos:", error); }
            setPhase('goalManagement');
        };
        loadGoals();
    }, [user, activityId]); // <-- MODIFICADO: Se elimina urls de las dependencias
    
    useEffect(() => {
        if (phase === 'reflection' && beliefInputRef.current) beliefInputRef.current.focus();
    }, [phase]);

    const updateGoalsState = (savedGoal) => {
        setAllGoals(prev => {
            const existingIndex = prev.findIndex(g => g.id === savedGoal.id);
            if (existingIndex > -1) {
                const newGoals = [...prev];
                newGoals[existingIndex] = savedGoal;
                return newGoals;
            }
            return [...prev, savedGoal];
        });
    };

    const handleSaveProgress = async (goalToSave) => {
        if (!user) return null;
        setIsSaving(true);
        try {
            // <-- MODIFICADO: Se usa UrlConfig directamente
            const response = await fetch(UrlConfig.activity_saveBeliefThreshold, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ activityId, ...goalToSave })
            });
            const result = await response.json();
            if (result.success) {
                updateGoalsState(result.data);
                return result.data;
            }
        } catch (error) { console.error("Error al guardar:", error); }
        finally { setIsSaving(false); }
        return null;
    };

    const handleDeleteGoal = async (goalId) => {
        if (!window.confirm("¿Estás seguro de que quieres borrar este objetivo? Esta acción es irreversible.")) return;
        // <-- MODIFICADO: Se usa UrlConfig directamente
        await fetch(UrlConfig.activity_deleteBeliefThresholdGoal, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify({ id: goalId })
        });
        setAllGoals(prev => prev.filter(g => g.id !== goalId));
    };

    const handleStartNewGoal = () => {
        setCurrentGoal({ id: null, manifestGoal: '', hesitations: [], currentTimeIndex: 0, perfectTimeFrameLabel: null });
        setPhase('goalInput');
    };

    const handleSelectGoal = (goal) => {
        setCurrentGoal(goal);
        if(goal.perfectTimeFrameLabel) {
            setPhase('summary');
        } else {
            setPhase('questioning');
        }
    };
    
    const handleDefineGoal = async () => {
        if (currentGoal.manifestGoal.trim().length <= 10) return;
        const savedGoal = await handleSaveProgress(currentGoal);
        if (savedGoal) { setCurrentGoal(savedGoal); setPhase('questioning'); }
    };
    
    const handleConfirmation = () => {
        if (currentGoal.currentTimeIndex < timeFrames.length - 1) {
            setCurrentGoal(prev => ({ ...prev, currentTimeIndex: prev.currentTimeIndex + 1 }));
        } else {
            setPhase('summary');
        }
    };

    const handleHesitation = () => {
        const newHesitation = { timeFrame: timeFrames[currentGoal.currentTimeIndex], belief: '' };
        setCurrentGoal(prev => ({ ...prev, hesitations: [...prev.hesitations, newHesitation] }));
        setPhase('reflection');
    };
    
    const handleBeliefChange = (e) => {
        const updatedHesitations = [...currentGoal.hesitations];
        updatedHesitations[updatedHesitations.length - 1].belief = e.target.value;
        setCurrentGoal(prev => ({ ...prev, hesitations: updatedHesitations }));
    };

    const handleLetGoOfBelief = async () => {
        const lastHesitation = currentGoal.hesitations[currentGoal.hesitations.length - 1];
        if (!lastHesitation || lastHesitation.belief.trim() === '') return;
        
        const savedGoal = await handleSaveProgress(currentGoal);
        if (savedGoal) {
            const updatedGoalWithNextStep = {
                ...savedGoal,
                currentTimeIndex: savedGoal.currentTimeIndex < timeFrames.length - 1 ? savedGoal.currentTimeIndex + 1 : savedGoal.currentTimeIndex
            };
             setCurrentGoal(updatedGoalWithNextStep);

            if (savedGoal.currentTimeIndex < timeFrames.length - 1) {
                setPhase('questioning');
            } else {
                setPhase('summary');
            }
        }
    };
    
    const handleSetPerfectTime = async () => {
        const perfectTime = timeFrames[currentGoal.currentTimeIndex];
        const updatedGoal = { ...currentGoal, perfectTimeFrameLabel: perfectTime.label, perfectTimeFrameYears: perfectTime.years };
        const savedGoal = await handleSaveProgress(updatedGoal);
        if (savedGoal) { setCurrentGoal(savedGoal); setPhase('summary'); }
    };

    const handleRedo = async () => {
        const resetGoal = { ...currentGoal, hesitations: [], currentTimeIndex: 0, perfectTimeFrameLabel: null, perfectTimeFrameYears: null };
        const savedGoal = await handleSaveProgress(resetGoal);
        if (savedGoal) { setCurrentGoal(savedGoal); setPhase('questioning'); }
    };

    // --- Componentes de Renderizado ---
    if (phase === 'loading') { return <div className="text-center p-8"><p className="text-yellow-400 animate-pulse">Cargando...</p></div>; }

    const renderGoalManagement = () => (
        <div className="animate-fade-in p-4 sm:p-8 bg-gray-900 rounded-xl border border-yellow-500/30">
            <h2 className="text-3xl font-bold mb-2 text-center">Tus Objetivos de Manifestación</h2>
            <p className="text-gray-400 text-center mb-8">Gestiona tus objetivos para el ejercicio "El Umbral de la Creencia".</p>
            <div className="space-y-4 mb-8">
                {allGoals.length > 0 ? allGoals.map(goal => (
                    <div key={goal.id} className="bg-gray-800 p-4 rounded-lg flex items-center justify-between gap-4">
                        <div className="flex-grow cursor-pointer" onClick={() => handleSelectGoal(goal)}>
                            <p className="font-bold text-lg text-white">{goal.manifestGoal}</p>
                            {goal.perfectTimeFrameLabel ? <p className="text-xs font-bold text-green-400 flex items-center gap-1.5"><CheckCircleIcon className="w-4 h-4" /> ¡Tiempo perfecto: {goal.perfectTimeFrameLabel}!</p>
                            : <p className="text-xs text-yellow-400">En progreso...</p>}
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-2">
                             <button onClick={() => handleSelectGoal(goal)} className="bg-blue-600 text-white font-semibold p-2 rounded-md hover:bg-blue-500 transition-colors" title="Continuar"><PlayIcon className="w-5 h-5"/></button>
                            <button onClick={() => handleDeleteGoal(goal.id)} className="bg-red-800 text-white font-semibold p-2 rounded-md hover:bg-red-700" title="Borrar"><TrashIcon className="w-5 h-5"/></button>
                        </div>
                    </div>
                )) : <p className="text-center text-gray-500 italic">Aún no has añadido ningún objetivo.</p>}
            </div>
            <button onClick={handleStartNewGoal} className="w-full flex items-center justify-center gap-2 bg-green-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-500"><PlusCircleIcon className="w-6 h-6"/>Empezar un Nuevo Objetivo</button>
        </div>
    );
    
    const renderGoalInput = () => (
        <div className="relative text-center animate-fade-in p-8 bg-gray-900 rounded-xl border border-purple-500/30">
            <button onClick={() => setPhase('goalManagement')} className="absolute top-4 left-4 flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeftIcon className="w-4 h-4"/> Volver</button>
            <QuestionMarkCircleIcon className="w-16 h-16 text-purple-400 mx-auto mb-4"/>
            <h2 className="text-3xl font-bold mb-2">¿Qué deseas manifestar?</h2>
            <p className="text-gray-400 mb-6">Sé específico y en tiempo presente.</p>
            <textarea className="w-full max-w-2xl mx-auto h-32 p-4 bg-gray-800 border border-gray-700 rounded-md text-white" placeholder='Ej: "Estoy disfrutando de una relación de pareja sana y amorosa".' value={currentGoal.manifestGoal} onChange={(e) => setCurrentGoal(prev => ({ ...prev, manifestGoal: e.target.value }))}/>
            <button onClick={handleDefineGoal} disabled={!currentGoal?.manifestGoal || currentGoal.manifestGoal.trim().length <= 10 || isSaving} className="mt-6 bg-purple-500 text-white font-bold py-3 px-8 rounded-lg hover:bg-purple-600 disabled:opacity-50">{isSaving ? 'Guardando...' : 'Definir y Continuar'}</button>
        </div>
    );

    const renderQuestioning = () => (
        <div className="relative animate-fade-in p-8 bg-gray-900 rounded-xl border border-cyan-500/30 text-center">
            <button onClick={() => setPhase('goalManagement')} className="absolute top-4 left-4 flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeftIcon className="w-4 h-4"/> Volver</button>
            <p className="text-gray-400 mb-2 mt-8">Tu objetivo:</p>
            <h3 className="text-2xl font-bold text-white mb-6 italic">"{currentGoal.manifestGoal}"</h3>
            <p className="text-xl text-gray-300 mb-8">¿Crees que podrías manifestar esto en...?</p>
            <h2 className="text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-500 mb-10 animate-pulse">{timeFrames[currentGoal.currentTimeIndex]?.label || '...'}</h2>
            <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
                <button onClick={handleHesitation} className="w-full md:w-auto bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400">Uhm... dudo</button>
                <button onClick={handleConfirmation} className="w-full md:w-auto bg-green-500 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-400">Sí, Absolutamente</button>
                <button onClick={handleSetPerfectTime} disabled={isSaving} className="w-full md:w-auto bg-blue-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-blue-500 flex items-center justify-center gap-2"><StarIcon className="w-5 h-5"/> ¡Este tiempo es perfecto!</button>
            </div>
             <p className="mt-8 text-gray-500 text-sm">Sé honesto. La primera sensación es la que cuenta.</p>
        </div>
    );
    
    const renderReflection = () => {
        const hesitation = currentGoal.hesitations[currentGoal.hesitations.length - 1] || {};
        return (
            <div className="relative animate-fade-in p-8 bg-gray-900 rounded-xl border border-yellow-500/30">
                <button onClick={() => setPhase('questioning')} className="absolute top-4 left-4 flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeftIcon className="w-4 h-4"/> Volver</button>
                <h2 className="text-3xl font-bold text-center text-yellow-400">¡Ahí está! Un límite.</h2>
                <p className="text-center text-gray-300 mt-2 mb-8">Dudaste en <strong>{hesitation.timeFrame?.label}</strong>. <br/>¿Qué tendrías que creer que es verdad para que te tome ese tiempo?</p>
                <textarea ref={beliefInputRef} className="w-full h-32 p-4 bg-gray-800 border border-gray-700 rounded-md text-white" placeholder="Ej: 'No soy lo suficientemente bueno', 'Necesito más dinero primero'..." value={hesitation.belief || ''} onChange={handleBeliefChange} />
                <div className="text-center mt-6">
                    <p className="text-gray-400 mb-4">¿Puedes ver que es una perspectiva, no una verdad, y soltarla?</p>
                    <button onClick={handleLetGoOfBelief} disabled={(hesitation.belief || '').trim() === '' || isSaving} className="bg-purple-500 text-white font-bold py-3 px-8 rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center gap-2 mx-auto"><ArrowPathIcon className="w-5 h-5"/>{isSaving ? 'Guardando...' : 'Sí, la suelto y continúo'}</button>
                </div>
            </div>
        )
    };

    const renderSummary = () => (
         <div className="animate-fade-in p-8 bg-gray-900 rounded-xl border border-green-500/30 text-center">
            <CheckCircleIcon className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4">Resumen del Objetivo</h2>
            <p className="text-gray-400 mb-6 italic">"{currentGoal.manifestGoal}"</p>
            {currentGoal.perfectTimeFrameLabel && <div className="bg-blue-900/50 border border-blue-500 rounded-lg p-4 max-w-md mx-auto mb-6"><p className="font-semibold text-blue-300">Has definido tu tiempo ideal como:</p><p className="text-2xl font-bold text-white">{currentGoal.perfectTimeFrameLabel}</p></div>}
            {currentGoal.hesitations.length > 0 ? <>
                <p className="text-gray-300 mb-6">Identificaste las siguientes creencias limitantes:</p>
                <div className="space-y-4 text-left max-w-2xl mx-auto">{currentGoal.hesitations.map((item, index) => <div key={index} className="bg-gray-800 p-4 rounded-lg"><p className="text-sm font-bold text-yellow-400">Límite en: {item.timeFrame.label}</p><p className="text-lg text-white mt-1 italic">"{item.belief}"</p></div>)}</div>
            </> : <p className="text-gray-300 mb-8 max-w-2xl mx-auto">¡Felicidades! No has encontrado bloqueos de tiempo para este objetivo.</p>}
            <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button onClick={handleRedo} disabled={isSaving} className="flex items-center justify-center gap-2 w-full sm:w-auto bg-gray-700 text-gray-300 font-bold py-3 px-8 rounded-lg hover:bg-gray-600"><ArrowPathIcon className="w-5 h-5"/>Empezar de Nuevo</button>
                <button onClick={() => setPhase('goalManagement')} className="w-full sm:w-auto bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400">Ver mis Otros Objetivos</button>
            </div>
        </div>
    );
    
    switch (phase) {
        case 'goalManagement': return renderGoalManagement();
        case 'goalInput': return renderGoalInput();
        case 'questioning': return renderQuestioning();
        case 'reflection': return renderReflection();
        case 'summary': return renderSummary();
        default: return <div className="text-center p-8"><p className="text-yellow-400 animate-pulse">Cargando...</p></div>;
    }
};

export default BeliefThresholdActivity;